import BasicModal from "@/components/basic-modal";
import UIButton from "@/components/ui-controls/button";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import IconWithDetails from "./Icon-with-details";

import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { getLeadInfoById } from "@/store/slices/lead/crmLeadSlice";
import {
  getAppointments,
  getCalendars,
} from "@/store/slices/appointment/appointmentSlice";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import { AttendeeStatus, Enums, FeatureKeys } from "@/services/dropdown-enums";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import useFeatures from "@/hooks/useFeatures";

import { Get_Leads_Grid } from "@/modules/crm/leads/graphql-queries";
import { Get_Appointments_By_Lead_Id } from "@/modules/crm/leads/graphql-queries/appointment-scheduling";
import moment from "moment";
import { faClock } from "@fortawesome/free-regular-svg-icons/faClock";
import { faCalendarAlt } from "@fortawesome/free-regular-svg-icons/faCalendarAlt";
import { faAddressCard, faUser } from "@fortawesome/free-regular-svg-icons";
import {
  faLocationDot,
  faPhone,
  faLink,
  faCommentDots,
  faVideo,
  faCodeBranch,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import useAuthProvider from "@/modules/crm/settings/hooks/useAuthProvider";
import { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { getBlazeoLeadGuid, getRoutingRows } from "./routing-helpers";
import { LeadModel } from "@blazeo.com/calendar-client";

const unwrapLeadResponse = (res) => {
  if (!res) return null;
  if (res.firstName || res.id || res.leadId) return res;
  return res?.leadDetails?.[0] || res?.leadDetails || null;
};

/** Guest fields on the appointment — shown immediately while lead verification runs. */
const leadFromAppointmentGuest = (appointment = {}) => {
  const leadId =
    getBlazeoLeadGuid(appointment?.leadId, appointment?.LeadId) ||
    appointment?.leadId ||
    appointment?.LeadId;
  const name = (
    appointment?.visitorName ||
    appointment?.name ||
    appointment?.guest ||
    ""
  ).trim();
  if (!leadId && !name) return null;
  const [firstName, ...rest] = name.split(/\s+/);
  return {
    id: leadId || undefined,
    leadId: leadId || undefined,
    firstName: firstName || name,
    lastName: rest.join(" "),
    email: appointment?.visitorEmail || appointment?.email || "",
  };
};

const formatLeadName = (lead) => {
  if (!lead) return "";
  if (lead.firstName) {
    return `${lead.firstName} ${lead.lastName || ""}`.trim();
  }
  const name = (lead.name || "").trim();
  if (!name) return "";
  const [firstName, ...rest] = name.split(/\s+/);
  return rest.length ? `${firstName} ${rest.join(" ")}` : firstName;
};

const appointmentBelongsToLead = (items = [], currentAppointment = {}) =>
  items.some(
    (appt) =>
      (currentAppointment?.id != null &&
        appt?.id != null &&
        String(appt.id) === String(currentAppointment.id)) ||
      (currentAppointment?.eventId &&
        appt?.eventId &&
        String(appt.eventId) === String(currentAppointment.eventId))
  );

const getLabelVariant = (selected) => {
  const status = selected?.status;
  const statusMap = {
    [AttendeeStatus.Accepted]: {
      variant: "primary",
      label: "Scheduled",
      color: "default",
    },
    [AttendeeStatus.Declined]: {
      variant: "error",
      label: "Cancelled",
      color: "error",
    },
    [AttendeeStatus.None]: {
      variant: "success",
      label: "Completed",
      color: "success",
    },
    [AttendeeStatus.Canceled]: {
      variant: "error",
      label: "Cancelled",
      color: "error",
    },
    [AttendeeStatus.Tentative]: {
      variant: "primary",
      label: "Scheduled",
      color: "default",
    },
    1: { variant: "primary", label: "Scheduled", color: "default" },
    2: { variant: "warning", label: "Re-scheduled", color: "warning" },
    3: { variant: "success", label: "Completed", color: "success" },
    4: { variant: "error", label: "Cancelled", color: "error" },
    complete: { variant: "success", label: "Completed", color: "success" },
    completed: { variant: "success", label: "Completed", color: "success" },
    canceled: { variant: "error", label: "Cancelled", color: "error" },
  };

  return statusMap[status] || { variant: "default", label: "Unknown" };
};

const ViewAppointment = ({
  appointment = {},
  handleAppointmentUpdate,
  btnLoading = false,
  open = false,
  setOpen = () => {},
  viewOnly = false,
  eventActions,
}) => {
  const [mappedAppointments, setMappedAppointments] = useState({});

  const { query } = useRouter();
  const leadInfo = useSelector(getLeadInfoById)?.[query?.id];
  const fetchedAppointments = useSelector(getAppointments);
  const currentAppointment =
    fetchedAppointments.find((app) => app.id == appointment?.id) || appointment;

  const calendars = useSelector(getCalendars);
  const user = useSelector(getLoggedinUser);
  const isSchedulingUser = user?.RoleType === Enums.Roles.SchedulingUser;
  const router = useRouter();
  const { fetchCalendars, calendarLoading } = useAuthProvider({
    authType: "Calendar",
  });
  const { client: dataClient } = useCRMDataClient();
  const { hasFeature } = useFeatures();
  const isBasic = !hasFeature(FeatureKeys.CRM);
  const [resolvedLead, setResolvedLead] = useState(null);
  const [routingRows, setRoutingRows] = useState([]);

  useEffect(() => {
    if (currentAppointment?.flowLoaded) {
      setRoutingRows(currentAppointment.routingRows || []);
      return;
    }

    if (!open) {
      setRoutingRows([]);
      return;
    }

    const eventId = currentAppointment?.eventId || currentAppointment?.id;
    if (!eventId) {
      setRoutingRows([]);
      return;
    }

    let cancelled = false;

    return () => {
      cancelled = true;
    };
  }, [
    open,
    currentAppointment?.id,
    currentAppointment?.eventId,
    currentAppointment?.flowLoaded,
    currentAppointment?.routingRows,
    dataClient,
  ]);

  const [leadLoading, setLeadLoading] = useState(false);
  const [isLeadDeleted, setIsLeadDeleted] = useState(false);
  const provisionalLead = useMemo(
    () => leadFromAppointmentGuest(currentAppointment),
    [
      currentAppointment?.id,
      currentAppointment?.eventId,
      currentAppointment?.leadId,
      currentAppointment?.LeadId,
      currentAppointment?.visitorName,
      currentAppointment?.visitorEmail,
      currentAppointment?.name,
      currentAppointment?.guest,
      currentAppointment?.email,
    ]
  );
  const verifiedLead = leadInfo || (isLeadDeleted ? null : resolvedLead);
  const isLeadVerifying = leadLoading && !leadInfo;
  const displayLead = verifiedLead;

  useEffect(() => {
    if (leadInfo) {
      setResolvedLead(null);
      setIsLeadDeleted(false);
      setLeadLoading(false);
      return;
    }

    const email = currentAppointment?.visitorEmail;
    if (!open || !email) {
      setResolvedLead(null);
      setIsLeadDeleted(false);
      setLeadLoading(false);
      return;
    }

    let cancelled = false;
    setLeadLoading(true);
    setIsLeadDeleted(false);

    (async () => {
      try {
        const blazeoRes = await LeadModel.getByEmail(email, user?.CompanyKey, {
          all: true,
        });
        if (cancelled) return;

        const blazeoLeads = Array.isArray(blazeoRes)
          ? blazeoRes
          : blazeoRes
            ? [blazeoRes]
            : [];
        const liveLead = blazeoLeads.find((lead) => lead && !lead.isDeleted);
        const blazeoLeadId = getBlazeoLeadGuid(
          liveLead?.leadId,
          liveLead?.LeadId
        );

        if (!liveLead || !blazeoLeadId) {
          setResolvedLead(null);
          setIsLeadDeleted(true);
          return;
        }

        const response = await dataClient.appointments.getAppointments({
          query: Get_Appointments_By_Lead_Id,
          leadId: blazeoLeadId,
        });
        if (cancelled) return;

        const items = (response?.appointments?.items || []).filter((appt) => {
          const appointmentLeadId = getBlazeoLeadGuid(appt.leadId, appt.LeadId);
          if (!appointmentLeadId) return true;
          return appointmentLeadId.toLowerCase() === blazeoLeadId.toLowerCase();
        });
        const belongsToLiveLead = appointmentBelongsToLead(
          items,
          currentAppointment
        );

        if (!belongsToLiveLead) {
          setResolvedLead(null);
          setIsLeadDeleted(true);
          return;
        }

        if (isBasic) {
          setResolvedLead(liveLead);
          setIsLeadDeleted(false);
          return;
        }

        try {
          const crmRes = await dataClient.leads.getLeadByEmail(email, {
            query: Get_Leads_Grid,
            companyKey: user?.CompanyKey,
          });
          if (cancelled) return;
          const crmLead = unwrapLeadResponse(crmRes);
          setResolvedLead(crmLead || null);
          setIsLeadDeleted(false);
        } catch (gqlErr) {
          console.error("Error fetching CRM lead by email:", gqlErr);
          if (cancelled) return;
          setResolvedLead(null);
          setIsLeadDeleted(false);
        }
      } catch (err) {
        console.error("Error resolving appointment lead:", err);
        if (!cancelled) {
          setResolvedLead(null);
          setIsLeadDeleted(true);
        }
      } finally {
        if (!cancelled) setLeadLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    leadInfo,
    currentAppointment?.id,
    currentAppointment?.eventId,
    currentAppointment?.visitorEmail,
    user?.CompanyKey,
    isBasic,
  ]);

  useEffect(() => {
    if (!calendars.length) {
      fetchCalendars();
    }
  }, []);

  const [status, setStatus] = useState(null);

  useEffect(() => {
    const label = getLabelVariant(currentAppointment);
    const startDate = moment.utc(currentAppointment?.startDate);
    const endDate = moment.utc(currentAppointment?.endDate);

    const iconMap = {
      phone: faPhone,
      link: faLink,
      location: faLocationDot,
      custom: faCommentDots,
      video: faVideo,
    };

    const mapAppointment = {
      ...currentAppointment,
      startTime: currentAppointment?.startTime || startDate?.format("h:mm A"),
      endTime: currentAppointment?.endTime || endDate?.format("h:mm A"),
      date: currentAppointment?.date || startDate?.format("ddd, MMMM D"),
      fullDate: `${startDate?.format("ddd, DD, MMM yyyy, h:mm A")} - ${endDate?.format("h:mm A")}`,
      locationText: currentAppointment?.locationText || "Default Location",
      locationIcon: iconMap[currentAppointment?.locationIcon] || faLocationDot,
      locationLink: currentAppointment?.locationLink || null,
    };

    setMappedAppointments(mapAppointment);
    setStatus(label);
  }, [currentAppointment]);

  const handleAppointmentModal = (type) => {
    const selected = fetchedAppointments.find(
      (app) => app.id == currentAppointment.id
    );
    handleAppointmentUpdate(selected, type, displayLead);
  };

  const isCancelled =
    currentAppointment?.status == AttendeeStatus.Canceled ||
    currentAppointment?.status == 4 ||
    currentAppointment?.status == "canceled";
  const isCompleted =
    currentAppointment?.status == AttendeeStatus.None ||
    currentAppointment?.status == 3 ||
    currentAppointment?.status == "complete" ||
    currentAppointment?.status == "completed";

  const modalButtons = (
    <>
      {viewOnly || isCancelled || isCompleted ? (
        <UIButton
          title={"Close"}
          onClick={() => setOpen(false)}
          variant={"outlined"}
          sx={{
            height: "38px",
            fontSize: "0.875rem",
            whiteSpace: "nowrap",
          }}
        />
      ) : null}
      {!viewOnly && !isCancelled && !isCompleted ? (
        <UIButton
          title={"Cancel Appointment"}
          onClick={() =>
            handleAppointmentModal(Enums.AppointmentStates.Canceled)
          }
          variant={"outlined"}
          sx={{ height: "38px", fontSize: "0.875rem", whiteSpace: "nowrap" }}
        />
      ) : null}
      {!viewOnly && !isCancelled && !isCompleted ? (
        <UIButton
          title={"Re-schedule"}
          onClick={() => handleAppointmentModal("reschedule")}
          variant={"outlined"}
          sx={{ height: "38px", fontSize: "0.875rem", whiteSpace: "nowrap" }}
        />
      ) : null}
      {!viewOnly && !isCancelled && !isCompleted ? (
        <UIButton
          title={"Mark as Completed"}
          onClick={() => handleAppointmentModal("complete")}
          variant={"contained"}
          sx={{
            height: "38px",
            fontSize: "0.875rem",
            whiteSpace: "nowrap",
            backgroundColor: "var(--palette-primary_button)",
            color: "#fff",
            "&:hover": {
              backgroundColor: "var(--palette-hover_color)",
            },
          }}
        />
      ) : null}
    </>
  );
  const resolvedCalendar = calendars.find(
    (c) =>
      c.uuid === currentAppointment?.calendarId ||
      c.calendarId === currentAppointment?.calendarId
  );
  const hostMember = resolvedCalendar?.members?.find(
    (m) =>
      m.id === currentAppointment?.participantId ||
      m.id === currentAppointment?.memberId ||
      m.memberId === currentAppointment?.participantId ||
      m.memberId === currentAppointment?.memberId ||
      m.userId === currentAppointment?.participantId ||
      m.userId === currentAppointment?.memberId
  );
  const hostName =
    hostMember?.name ||
    resolvedCalendar?.members?.[0]?.name ||
    mappedAppointments?.organizer ||
    "N/A";

  const verifiedLeadName = formatLeadName(verifiedLead);
  const provisionalName =
    formatLeadName(provisionalLead) ||
    mappedAppointments?.guest ||
    mappedAppointments?.visitorName ||
    "";
  const hasProvisionalName = Boolean(provisionalName?.trim());
  const fallbackGuest = provisionalName || "N/A";
  const canRouteToLead =
    !!verifiedLead?.firstName &&
    !isSchedulingUser &&
    !isLeadVerifying &&
    !isLeadDeleted;
  const leadSubHeadingText = isLeadDeleted && !isLeadVerifying
    ? `${fallbackGuest} (Deleted)`
    : !isLeadVerifying && verifiedLeadName
      ? verifiedLeadName
      : hasProvisionalName
        ? provisionalName
        : isLeadVerifying
          ? "Loading..."
          : verifiedLeadName || fallbackGuest;
  const showLeadSubHeadingText =
    !(isLeadVerifying && leadSubHeadingText === "Loading..." && !hasProvisionalName);
  const leadSubHeadingTrailing = isLeadVerifying ? (
    <CircularProgress
      size={14}
      thickness={5}
      aria-label="Verifying lead"
      sx={{ color: "var(--palette-grey_text)", flexShrink: 0 }}
    />
  ) : canRouteToLead ? (
    <GetFontAwesomeIcon
      icon={faArrowUpRightFromSquare}
      iconStyles={{
        fontSize: "0.75rem",
        marginRight: 0,
        color: "var(--palette-primary_main)",
      }}
    />
  ) : null;
  const leadSubHeading = leadSubHeadingTrailing || !showLeadSubHeadingText ? (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minHeight: 20,
      }}
    >
      {showLeadSubHeadingText ? <span>{leadSubHeadingText}</span> : null}
      {leadSubHeadingTrailing}
    </Box>
  ) : (
    leadSubHeadingText
  );

  const appointmentDetails = [
    {
      icon: faClock,
      subHeading: mappedAppointments?.fullDate,
      heading: `(${mappedAppointments?.timeZone || mappedAppointments?.selectedTimeZone || "N/A"})`,
      color: "var(--primary)",
    },
    {
      icon: faCalendarAlt,
      heading: "Appointment Type",
      subHeading: `${resolvedCalendar?.name || mappedAppointments?.serviceType || "N/A"}`,
      color: "var(--primary)",
    },
    {
      icon: faUser,
      heading: "Host",
      subHeading: hostName,
      color: "var(--primary)",
    },
    {
      icon: faAddressCard,
      heading: "Lead",
      subHeading: leadSubHeading,
      color: "var(--primary)",
      subHeadingRest: canRouteToLead
        ? {
            onClick: () => {
              const targetId = isBasic
                ? displayLead?.leadId || currentAppointment?.leadId
                : displayLead?.id || currentAppointment?.leadId;

              if (targetId) {
                router.push(`/crm/leads/view/${targetId}`);
              }
            },
          }
        : undefined,
      subHeadingSX: canRouteToLead
        ? {
            cursor: "pointer",
          }
        : {},
    },
    {
      icon: mappedAppointments?.locationIcon || faLocationDot,
      heading: "Meeting Location",
      subHeading: mappedAppointments?.locationLink ? (
        <a
          href={mappedAppointments.locationLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "var(--palette-secondary_button_text)",
            textDecoration: "underline",
          }}
        >
          {mappedAppointments.locationText}
        </a>
      ) : (
        mappedAppointments?.locationText || "Default Location"
      ),
      color: "var(--primary)",
    },
    ...(routingRows.length
      ? [
          {
            icon: faCodeBranch,
            heading: currentAppointment?.flowTitle || "Routing",
            alignItems: "flex-start",
            subHeading: (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {routingRows.map(({ key, label, value }) => (
                  <Typography
                    key={key || label}
                    variant="body2"
                    component="div"
                    sx={{ fontWeight: 400, lineHeight: 1.4 }}
                  >
                    {label}:{" "}
                    <Box component="span" sx={{ mx: 0.5 }}>
                      →
                    </Box>{" "}
                    {value}
                  </Typography>
                ))}
              </Box>
            ),
            color: "var(--primary)",
          },
        ]
      : []),
  ];

  return (
    <>
      <BasicModal
        open={open}
        title={"Appointment"}
        handleClose={() => setOpen(false)}
        saveBtnHidden={true}
        size="xs"
        customFooterButtons={modalButtons}
        showFooterDivider={!isCompleted}
        modalBodySx={{ padding: "1rem" }}
        footerLoading={btnLoading}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}
        >
          <Box className="d-flex justify-content-space-between align-items-center">
            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <Typography
                variant="h4"
                sx={{ color: "var(--palette-sub_heading)" }}
              >
                {currentAppointment?.title ||
                  currentAppointment?.name ||
                  resolvedCalendar?.name ||
                  ""}
              </Typography>
              <Typography
                sx={{
                  color: `var(--palette-${status?.color})`,
                  whiteSpace: "nowrap",
                  pr: 1,
                }}
              >
                {status?.label}
              </Typography>
            </Box>
            {eventActions ? (
              <Box className="d-flex" sx={{ gap: 1 }}>
                {eventActions(currentAppointment)
                  .filter((v) => v.name !== "Re-Schedule")
                  .map((ev, index) => {
                    return (
                      <GetFontAwesomeIcon
                        iconClass="cursor-pointer"
                        iconStyles={{
                          fontSize: 20,
                          color: "var(--palette-default)",
                        }}
                        icon={ev.icon}
                        key={index}
                        onClick={() => ev?.callback(currentAppointment)}
                      />
                    );
                  })}
              </Box>
            ) : null}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            {appointmentDetails.map((detail, index) => (
              <IconWithDetails key={index} {...detail} />
            ))}
          </Box>
        </Box>
      </BasicModal>
    </>
  );
};

export default ViewAppointment;
